import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ConfigProvider, theme } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { buildAntTheme, buildCssVars } from '../constants/constants';
import { useStorage, STORAGE_KEY } from './StorageContext';

interface ThemeContextValue {
  isDark:     boolean;
  toggleDark: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const storage = useStorage();

  const [isDark, setIsDark] = useState<boolean>(
    () => storage.get(STORAGE_KEY.theme) === 'dark'
  );

  useEffect(() => {
    storage.set(STORAGE_KEY.theme, isDark ? 'dark' : 'light');
    document.body.dataset.theme = isDark ? 'dark' : 'light';

    // body 배경·하락 행 강조 등 Ant 바깥 영역용 CSS 변수 주입
    const vars = buildCssVars(isDark);
    const root = document.documentElement;
    Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
  }, [isDark, storage]);

  const toggleDark = () => setIsDark((v) => !v);

  // constants.ts 의 PALETTE → resolveTokens 가 단일 출처.
  // Ant token + components 모두 동일한 토큰을 사용한다.
  const { token, components } = buildAntTheme(isDark);

  return (
    <ThemeContext.Provider value={{ isDark, toggleDark }}>
      <ConfigProvider
        locale={koKR}
        theme={{
          algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
          token,
          components,
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}